import { PrivacyChoicesButton } from './consent/PrivacyChoicesButton';

/**
 * Placeholder shown while a lazily-loaded route chunk is in flight.
 *
 * Reserves height so the app shell does not collapse and then jump when the
 * chunk lands, and announces itself politely so a screen reader hears that the
 * page is loading rather than sitting on silence. The pulse is suppressed under
 * `prefers-reduced-motion` via Tailwind's `motion-reduce` variant.
 */
export function RouteFallback({ label = 'Loading page…' }: { label?: string }) {
  return (
    <div role="status" aria-live="polite" className="space-y-4 py-2">
      <span className="sr-only">{label}</span>
      <div
        aria-hidden="true"
        className="h-8 w-64 animate-pulse rounded-lg bg-slate-800/70 motion-reduce:animate-none"
      />
      <div
        aria-hidden="true"
        className="h-4 w-96 max-w-full animate-pulse rounded-lg bg-slate-800/50 motion-reduce:animate-none"
      />
      <div
        aria-hidden="true"
        className="h-64 animate-pulse rounded-2xl border border-slate-800 bg-slate-900/60 motion-reduce:animate-none"
      />
    </div>
  );
}

/**
 * Full-screen variant for the boundary above the app shell, where there is no
 * surrounding chrome to hold the layout together yet.
 */
export function AppShellFallback({ label = 'Loading workspace…' }: { label?: string }) {
  return (
    <div className="min-h-dvh bg-slate-950 text-slate-100">
      <div className="mx-auto flex min-h-dvh max-w-4xl flex-col items-center justify-center gap-4 px-6">
        <div role="status" aria-live="polite" className="rounded-3xl border border-slate-800 bg-slate-900/80 px-6 py-5 text-sm text-slate-300">
          {label}
        </div>
        {/* Shown before the sidebar exists, and the app hides the SDK's floating launcher. */}
        <PrivacyChoicesButton variant="link" />
      </div>
    </div>
  );
}
